import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { useI18n } from "@/i18n";
import { PageHeader, EmptyState, useSortable } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, Package } from "lucide-react";
import { NONE, EMPTY_PRODUCT, cleanDims, totalCost } from "@/components/products/ProductFields";
import ProductsTable from "@/components/products/ProductsTable";
import { ProductCreateDialog, ProductEditDialog } from "@/components/products/ProductDialogs";

const variantPayload = (obj) => ({
  sku: obj.sku, ean: obj.ean, is_digital: !!obj.is_digital,
  cost_price_base: +obj.cost_price_base || 0,
  surcharges: (obj.surcharges || []).filter((s) => s.label || s.amount)
    .map((s) => ({ label: s.label || "", amount: +s.amount || 0 })),
  cost_price: totalCost(obj),
  price_net: +obj.price_net || 0, price_gross: +obj.price_gross || 0,
  price_net_second: obj.price_net_second === "" ? null : +obj.price_net_second,
  price_gross_second: obj.price_gross_second === "" || obj.price_gross_second == null
    ? null : +obj.price_gross_second,
  reorder_point: +obj.reorder_point || 0, tax_rate: +obj.tax_rate || 0,
  tax_code: obj.tax_code || null,
  units_per_carton: obj.units_per_carton === "" ? null : +obj.units_per_carton,
  hs_code: obj.hs_code, hts_code_us: obj.hts_code_us, country_of_origin: obj.country_of_origin,
  strap_option: !!obj.strap_option, expiry_tracked: !!obj.expiry_tracked, ...cleanDims(obj),
});

export default function Products() {
  const { user } = useAuth();
  const { t } = useI18n();
  const canEdit = can(user, "products:edit");
  const canCost = can(user, "finance:view");
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null); // {vid, ...fields}
  const [f, setF] = useState(EMPTY_PRODUCT);
  const [q, setQ] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [settings, setSettings] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);

  const load = useCallback(() => api.get("/products", { params: { include_archived: showArchived } })
    .then((r) => setRows(r.data)).catch(() => setRows([])), [showArchived]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/suppliers").then((r) => setSuppliers([...r.data].sort((a, b) => (a.name || "").localeCompare(b.name || "", "de")))).catch(() => {});
    api.get("/warehouses").then((r) => setWarehouses(r.data)).catch(() => {});
  }, []);

  const supMap = Object.fromEntries(suppliers.map((s) => [s.id, s.name]));
  const taxRates = settings?.tax_rates || [0, 7, 19];

  /** Anfangsbestand: eigener, vollwertiger Wareneingang. MHD bleibt hier bewusst optional. */
  const bookInitialStock = async (variantId) => {
    const qty = Number(f.initial_stock) || 0;
    if (qty <= 0 || !variantId) return;
    if (!warehouses[0]?.id) { toast.error(t("prod.initialStockNoWarehouse")); return; }
    try {
      await api.post("/purchase-receipts", {
        supplier_id: f.supplier_id && f.supplier_id !== NONE ? f.supplier_id : null,
        warehouse_id: warehouses[0].id, reference: t("prod.initialStockRef"),
        status: "eingegangen", bbd_waived: true, receipt_type: "initial_stock",
        lines: [{ variant_id: variantId, quantity: qty, unit_cost: totalCost(f),
          lot_number: f.initial_lot || "", best_before_date: f.initial_bbd || null }],
      });
      toast.success(t("prod.initialStockBooked", { qty }));
    } catch (e) { toast.error(`${t("prod.initialStockFailed")} ${apiError(e)}`); }
  };

  const save = async () => {
    try {
      const res = await api.post("/products", { sku: f.sku, name: f.name, type: "einzelprodukt",
        variants: [variantPayload(f)] });
      toast.success(t("prod.created"));
      await bookInitialStock(res.data?.variant_ids?.[0]);
      setOpen(false); setF(EMPTY_PRODUCT); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const openEdit = (p) => {
    const v = p.variants?.[0] || {};
    setEdit({ vid: v.id, sku: v.sku || "", product_name: p.name, ean: v.ean || "", is_digital: !!v.is_digital,
      cost_price_base: (v.cost_price_base != null ? v.cost_price_base : v.cost_price) ?? "",
      surcharges: (v.surcharges || []).map((s) => ({ _k: crypto.randomUUID(), label: s.label || "", amount: s.amount ?? "" })),
      price_net: v.price_net ?? "", price_gross: v.price_gross ?? "", price_net_second: v.price_net_second ?? "",
      price_gross_second: v.price_gross_second ?? "", reorder_point: v.reorder_point ?? 0,
      units_per_carton: v.units_per_carton ?? "",
      tax_rate: v.tax_rate ?? 19, tax_code: v.tax_code || "", hs_code: v.hs_code || "",
      hts_code_us: v.hts_code_us || "", country_of_origin: v.country_of_origin || "",
      strap_option: !!v.strap_option, expiry_tracked: !!v.expiry_tracked,
      supplier_id: v.supplier_id || NONE, length_cm: v.length_cm ?? "", width_cm: v.width_cm ?? "",
      height_cm: v.height_cm ?? "", diameter_cm: v.diameter_cm ?? "", dimension_uom: v.dimension_uom || "cm",
      weight_unit_value: v.weight_unit_value ?? "", weight_unit_uom: v.weight_unit_uom || "g",
      weight_carton_value: v.weight_carton_value ?? "", weight_carton_uom: v.weight_carton_uom || "kg" });
  };

  const saveEdit = async () => {
    try {
      await api.put(`/variants/${edit.vid}`, { product_name: edit.product_name, ...variantPayload(edit) });
      toast.success(t("prod.savedAudit")); setEdit(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const del = async (p) => {
    if (!window.confirm(t("prod.confirmDelete", { sku: p.sku }))) return;
    try { await api.delete(`/products/${p.id}`); toast.success(t("prod.deleted")); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const arch = async (p) => {
    try {
      await api.post(`/products/${p.id}/${p.archived ? "unarchive" : "archive"}`);
      toast.success(p.archived ? t("common.unarchive") : t("common.archived")); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const needle = q.toLowerCase();
  const filtered = (rows || []).filter((p) => !q
    || p.name.toLowerCase().includes(needle) || p.sku.toLowerCase().includes(needle)
    || (p.variants?.[0]?.ean || "").includes(q));
  const { sorted, sortKey, sortDir, toggle } = useSortable(filtered, {
    sku: (p) => p.sku, name: (p) => p.name,
    supplier: (p) => supMap[p.variants?.[0]?.supplier_id] || "",
    ean: (p) => p.variants?.[0]?.ean || "",
    hs: (p) => p.variants?.[0]?.hs_code || "",
    cost: (p) => p.variants?.[0]?.cost_price || 0,
    net: (p) => p.variants?.[0]?.price_net || 0,
  });

  return (
    <div className="flex h-full flex-col">
      <PageHeader title={t("prod.title")} subtitle={t("prod.subtitle")}
        actions={canEdit && (
          <Button data-testid="new-product-btn" onClick={() => setOpen(true)}><Plus className="h-4 w-4 mr-1.5" /> {t("prod.new")}</Button>
        )} />

      <div className="mb-4 flex items-center gap-4 shrink-0">
        <Input data-testid="product-search" placeholder={t("prod.searchPlaceholder")} value={q}
          onChange={(e) => setQ(e.target.value)} className="max-w-xs" />
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} data-testid="show-archived" />
          {t("common.showArchived")}
        </label>
      </div>

      {rows === null ? <div className="text-muted-foreground">{t("common.loading")}</div> :
        filtered.length === 0
          ? <EmptyState testId="products-empty" icon={Package} title={t("prod.emptyTitle")} hint={t("prod.emptyHint")} />
          : <ProductsTable rows={sorted} supMap={supMap} canEdit={canEdit} canCost={canCost} sortKey={sortKey} sortDir={sortDir}
              onSort={toggle} onEdit={openEdit} onArchive={arch} onDelete={del} />}

      <ProductCreateDialog open={open} onOpenChange={setOpen} f={f} setF={setF}
        suppliers={suppliers} taxRates={taxRates} onSave={save} />
      <ProductEditDialog edit={edit} setEdit={setEdit} suppliers={suppliers} taxRates={taxRates}
        onSave={saveEdit} onPricesApplied={load} />
    </div>
  );
}
